import { filter } from "lodash";
import PropTypes from "prop-types";
import React, { Fragment } from "react";

import { PropTypeShapes } from "../../types";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import preprocessorQuery from "../../utils/preprocessorQuery";
import layoutFunctions from "./layoutFunctions";

const rootTwo = Math.sqrt(2);

const limitMarkerWidth = (props, left, width, showDiamond) => {
  const totalLeft = props.totalRectLeft + left;
  let markerLeft = left;
  let markerWidth = width;

  // Adjust where the marker starts if we moved TimelineItemNode
  if (props.leftAdjustment) {
    markerLeft = markerLeft + props.leftAdjustment;
  }

  if (showDiamond && markerLeft < 0 && !props.draggingStartArrow) {
    markerLeft = 0;
  } else if (!showDiamond && totalLeft + markerWidth < props.viewportX) {
    // Marker is off screen
    markerWidth = 0;
    markerLeft = -100;
  } else if (!showDiamond && !props.draggingStartArrow) {
    // Limit marker size
    if (markerLeft < 0) {
      markerWidth = markerWidth + markerLeft;
      markerLeft = 0;
    }
    markerWidth = Math.min(props.canvasRange, markerWidth);
  }

  return {
    left: markerLeft,
    width: markerWidth
  };
};

const getRectStyle = (props, rect, showDiamond) => {
  let width = Math.max(0, rect.width);
  let left = rect.left;
  const top = rect.top;
  const height = rect.height;

  const markerAdjustments = limitMarkerWidth(props, left, width, showDiamond);
  left = markerAdjustments.left;
  width = markerAdjustments.width;

  return {
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
    opacity: props.isDragPreview ? `0.4` : `1`
  };
};

const TimelineItemMarker = props => {
  const isIos = preprocessorQuery.isIos();
  const layoutConstants = layoutFunctions.getLayoutConstants(props.fontSize, props.showItemCard);
  let width = Math.max(0, props.rect.width);
  let left = props.rect.left;
  const top = props.rect.top;
  const height = props.rect.height;
  const hasUncertainStart = props.uncertainStartRect.width;
  const hasUncertainEnd = props.uncertainEndRect.width;

  let style = {
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`
  };
  let endDateArrowLeft = width + left + (props.leftAdjustment ? props.leftAdjustment : 0);
  let startDateArrowLeft = props.showItemCard ? left * -1 : height * -1;

  if (hasUncertainStart) {
    style.borderTopLeftRadius = 0;
    style.borderBottomLeftRadius = 0;
  }
  if (hasUncertainEnd) {
    style.borderTopRightRadius = 0;
    style.borderBottomRightRadius = 0;
  }

  // Use a rotated square instead of the normal square
  // See ATR-419
  const showDiamond = width <= height && !hasUncertainStart && !hasUncertainEnd && !props.ongoing;
  if (showDiamond) {
    const zeroWidth = height / rootTwo;
    style.top = `${top + (height - zeroWidth) / 2}px`;
    style.width = `${zeroWidth}px`;
    style.height = `${zeroWidth}px`;

    endDateArrowLeft = zeroWidth + left;
  }

  // Limit size
  const rectStyle = getRectStyle(props, props.rect, showDiamond);
  style.left = rectStyle.left;
  if (!showDiamond) {
    style.width = rectStyle.width;
  }

  let markerClass = !showDiamond ? "timelineEventMarker" : "timelineEventMarkerZeroDuration";
  if (props.ongoing) {
    markerClass += ` timelineEventOngoingMarker`;
  }

  if (props.reportMarkerPosition) {
    props.reportMarkerPosition((top + height) / 2, (left + width) / 2);
  }

  // Uncertain start markers
  let uncertainStartMarker = null;
  let uncertainStartClasses = ["timelineEventMarker", "timelineEventUncertainStartMarker", props.color];
  if (hasUncertainStart) {
    const uncertainStartStyle = getRectStyle(props, props.uncertainStartRect);
    if (!isIos && props.uncertainStartRect.width < props.viewportWidth) {
      uncertainStartClasses.push("gradient");
    }
    uncertainStartMarker = <div className={uncertainStartClasses.join(" ")} style={uncertainStartStyle} />;

    startDateArrowLeft = props.uncertainStartRect.left * -1 - (!props.showItemCard ? height : 0);
  }

  let uncertainEndMarker = null;
  let uncertainEndClasses = ["timelineEventMarker", "timelineEventUncertainEndMarker", props.color];
  if (hasUncertainEnd) {
    const uncertainEndStyle = getRectStyle(props, props.uncertainEndRect);
    if (!isIos && props.uncertainEndRect.width < props.viewportWidth) {
      uncertainEndClasses.push("gradient");
    }
    uncertainEndMarker = <div className={uncertainEndClasses.join(" ")} style={uncertainEndStyle} />;

    endDateArrowLeft += props.uncertainEndRect.width;
  }

  // Preferred range markers
  let parentPreferredStartMarker = null;
  if (props.startRectWithChildren.width) {
    const preferredStartStyle = getRectStyle(props, props.startRectWithChildren);
    parentPreferredStartMarker = (
      <div
        className={`timelineEventMarker timelineEventPreferredStartMarker ${props.color}`}
        style={preferredStartStyle}
      />
    );

    startDateArrowLeft =
      props.startRectWithChildren.width +
      (props.showItemCard && showDiamond ? layoutConstants.MARKER_RIGHT_MARGIN : 0) -
      (!props.showItemCard && showDiamond ? height / 2 : 0) -
      (!props.showItemCard && !showDiamond ? height : 0);
  }

  let parentPreferredEndMarker = null;
  if (props.endRectWithChildren.width) {
    const preferredEndStyle = getRectStyle(props, props.endRectWithChildren);
    parentPreferredEndMarker = (
      <div className={`timelineEventMarker timelineEventPreferredEndMarker ${props.color}`} style={preferredEndStyle} />
    );
  }

  let endDateArrow, startDateArrow;
  //#!if iOS !== true && readonly !== true
// if (props.allowDragEnd && !props.draggingStartArrow && !props.draggingEndArrow) {
// if (!props.ongoing) {
// endDateArrow = (
// <div
// className="endDateArrow"
// style={{
// top: `${top / 2}px`,
// left: `${endDateArrowLeft}px`,
// height: `${height + top}px`,
// width: `${height}px`
// }}
// onMouseDown={props.onRightResizeMouseDown}
// onTouchStart={props.onRightResizeMouseDown}
// >
// <svg className="icon iconMono">
// <use xlinkHref="#contextLensRight" />
// </svg>
// </div>
// );
// }
// 
// startDateArrow = (
// <div
// className="startDateArrow"
// style={{
// top: `${top / 2}px`,
// left: `${startDateArrowLeft}px`,
// height: `${height + top}px`,
// width: `${height}px`
// }}
// onMouseDown={props.onLeftResizeMouseDown}
// onTouchStart={props.onLeftResizeMouseDown}
// >
// <svg className="icon iconMono">
// <use xlinkHref="#contextLensLeft" />
// </svg>
// </div>
// );
// }
  //#!endif

  let parentShadowMarker = null;
  if (props.parentShadowMarkerRect.width) {
    let shadowStyle = getRectStyle(props, props.parentShadowMarkerRect);
    if (showDiamond) {
      const zeroWidth = props.parentShadowMarkerRect.height / rootTwo;
      shadowStyle.top = `${props.parentShadowMarkerRect.top + (props.parentShadowMarkerRect.height - zeroWidth) / 2}px`;
      shadowStyle.width = `${zeroWidth}px`;
      shadowStyle.height = `${zeroWidth}px`;
    }
    parentShadowMarker = <div className={`${markerClass} parentShadow ${props.color}`} style={shadowStyle} />;
  }

  const label = getLabelWithUntitled(props.label);
  const markerSpan =
    props.showLabel && !showDiamond ? (
      <div className="timelineMarkerLabelWrapper">
        <span className={props.label ? "" : "isUntitled"}>{label}</span>
      </div>
    ) : null;

  const mainClassList = filter(
    [
      markerClass,
      props.color,
      hasUncertainStart ? "hasUncertainStart" : null,
      hasUncertainEnd ? "hasUncertainEnd" : null,
      props.ongoing ? "isOngoing" : null
    ],
    c => c
  );

  return (
    <Fragment>
      {parentShadowMarker}
      {parentPreferredStartMarker}
      {uncertainStartMarker}
      <div className={`${mainClassList.join(" ")}`} style={style} title={props.label}>
        {markerSpan}
      </div>
      {uncertainEndMarker}
      {parentPreferredEndMarker}
      {endDateArrow}
      {startDateArrow}
    </Fragment>
  );
};

TimelineItemMarker.propTypes = {
  // Any special instructions when placing the data?
  rect: PropTypeShapes.flatRect.isRequired,
  color: PropTypes.string.isRequired,
  label: PropTypes.string,
  showLabel: PropTypes.bool,
  hasChildren: PropTypes.bool.isRequired,
  fontSize: PropTypes.number.isRequired,
  reportMarkerPosition: PropTypes.func,
  uncertainStartRect: PropTypeShapes.flatRect.isRequired,
  uncertainEndRect: PropTypeShapes.flatRect.isRequired,
  ongoing: PropTypes.bool.isRequired,
  startRectWithChildren: PropTypeShapes.flatRect.isRequired,
  endRectWithChildren: PropTypeShapes.flatRect.isRequired,
  parentShadowMarkerRect: PropTypeShapes.flatRect.isRequired,
  isDragPreview: PropTypes.bool,
  totalRectLeft: PropTypes.number.isRequired,
  virtualOffsetX: PropTypes.number.isRequired,
  viewportWidth: PropTypes.number.isRequired,
  viewportX: PropTypes.number.isRequired,
  canvasRange: PropTypes.number.isRequired,
  leftAdjustment: PropTypes.number.isRequired,
  onLeftResizeMouseDown: PropTypes.func,
  onRightResizeMouseDown: PropTypes.func,
  allowDragEnd: PropTypes.bool.isRequired,
  showItemCard: PropTypes.bool.isRequired,
  draggingStartArrow: PropTypes.bool,
  draggingEndArrow: PropTypes.bool
};

export default TimelineItemMarker;
